import { useEffect } from 'react';
import { useLocation, useMatches } from 'react-router';

/** Lets rapid staging churn settle before spending a request on a warm. */
const WARM_DELAY_MS = 300;

/**
 * Warms the browser's HTTP cache with the route data for `page` so a
 * subsequent navigation there serves from cache.
 *
 * This deliberately avoids <link rel="prefetch"> (PrefetchPageLinks): Safari
 * ignores the tag entirely, and Chromium stores link-prefetched responses as
 * Sec-Purpose-flagged cache entries that later navigations reuse
 * inconsistently. A plain fetch primes an ordinary HTTP-cache entry instead;
 * the X-Purpose header lets the server mark the response cacheable (see
 * handleDataRequest in entry.server).
 *
 * The warmed URL must byte-match React Router's single-fetch request, whose
 * `_routes` param lists the routes that revalidate. That mirrors this app's
 * revalidation policy: root never revalidates; the layouts skip search-only
 * changes but revalidate on pathname changes (a staged category commit). If
 * the policy and this list ever drift apart, navigations just stop matching
 * the warmed URL — the cost is an unused background request, never a wrong
 * response.
 */
export const StagedDestinationWarmer = ({ page }: { page: string }) => {
    const location = useLocation();
    const matches = useMatches();
    // Everything below root, in match order — RR joins _routes in that order.
    const revalidatingRouteIds = (
        new URL(page, 'https://x').pathname === location.pathname
            ? [matches[matches.length - 1]?.id]
            : matches.slice(1).map((match) => match.id)
    )
        .filter((id): id is string => !!id)
        .join(',');

    useEffect(() => {
        if (!revalidatingRouteIds) return;

        const timeout = setTimeout(() => {
            const url = new URL(page, window.location.origin);
            url.pathname += '.data';
            url.searchParams.set('_routes', revalidatingRouteIds);
            fetch(url, { headers: { 'X-Purpose': 'prefetch' } })
                // Drain the body so the cache entry completes.
                .then((response) => response.arrayBuffer())
                .catch(() => {
                    // Cache warming only — the navigation fetches for real.
                });
        }, WARM_DELAY_MS);
        return () => clearTimeout(timeout);
    }, [page, revalidatingRouteIds]);

    return null;
};
